import { pageTitle } from '@palaccount/config';
import type { BankAccountResponse } from '@palaccount/contracts';
import { CheckCircle2, Lock, Plus } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';

import { Amount, Ltr } from '@/components/amount';
import { ConfirmBox } from '@/components/confirm-box';
import { Gated } from '@/components/permission-gate';
import { Pager } from '@/components/pager';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/native-select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { CurrencyField } from '@/components/ui/currency-field';
import { Textarea } from '@/components/ui/textarea';
import { useAccounts } from '@/features/accounts/api';
import { currencyNotOffered, useCurrencyPickerCodes } from '@/features/fx/api';
import { usePagedState, usePageRange } from '@/lib/paging';
import { useCan } from '@/lib/permissions';
import { localizeServerCause } from '@/lib/server-messages';
import { useBaseCurrency, useDocumentDate } from '@/lib/session';

import {
  useAddAdjustment,
  useBankAccounts,
  useCompleteReconciliation,
  useCreateBankAccount,
  useReconciliation,
  useReconciliationHistory,
  useReopenReconciliation,
  useSetStatement,
  useSetTicks,
  useStartReconciliation,
  useUpdateBankAccount,
} from './api';
import { BankingTabs } from './banking-tabs';

const ACCOUNT_TYPES = ['bank', 'cash', 'petty_cash', 'credit_card'] as const;

/**
 * FIN-09 — Bank Accounts: the account list on the leading side, a tabbed
 * detail pane — Details (every field EDITABLE, the prototype's read-only tab
 * was a listed defect) and Reconciliation (the §5d workbench: tick the books
 * against the statement until the difference is zero, adjust for fees or
 * interest, lock).
 *
 * The dashboard deep link lands here as ?account=<id>&tab=reconciliation —
 * "the only deep link of its kind in the product" (FIN-09 §3).
 */
export function BankAccountsPage() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: accounts, isPending } = useBankAccounts();

  useEffect(() => {
    document.title = pageTitle(t('banking.accountsTitle'));
  }, [t]);

  const selectedId = searchParams.get('account');
  const selected =
    (accounts ?? []).find((account) => account.id === selectedId) ?? (accounts ?? [])[0];
  const tab = searchParams.get('tab') === 'reconciliation' ? 'reconciliation' : 'details';
  const [creating, setCreating] = useState(false);

  const open = (accountId: string, nextTab?: string) => {
    setSearchParams((params) => {
      params.set('account', accountId);
      if (nextTab) params.set('tab', nextTab);
      return params;
    });
  };

  return (
    <div className="grid gap-4">
      <BankingTabs />
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="me-auto text-lg font-bold">{t('banking.accountsTitle')}</h2>
        <Gated permission="banking.create" reasonKey="perm.cannot.createBankAccounts">
          <Button onClick={() => setCreating((value) => !value)}>
            <Plus aria-hidden /> {t('common.new')}
          </Button>
        </Gated>
      </div>

      {creating ? <NewAccountForm onDone={() => setCreating(false)} /> : null}

      <div className="grid gap-4 lg:grid-cols-[320px_1fr]">
        <div className="grid content-start gap-2">
          {isPending ? <p className="text-sm text-muted-foreground">{t('common.loading')}</p> : null}
          {(accounts ?? []).map((account) => (
            <button
              key={account.id}
              type="button"
              data-testid={`bank-account-${account.name}`}
              onClick={() => open(account.id)}
              className={`rounded-lg border p-3 text-start ${
                account.id === selected?.id ? 'border-primary ring-2 ring-primary/30' : 'hover:bg-muted/50'
              } ${account.isActive ? '' : 'opacity-60'}`}
            >
              <span className="flex items-center justify-between gap-2 text-sm font-semibold">
                {account.name}
                <span className="text-xs font-normal text-muted-foreground">
                  {t(`banking.type.${account.accountType}`)}
                </span>
              </span>
              <span className="mt-1 flex items-center justify-between gap-2 text-xs text-muted-foreground">
                <span>{account.bankName ?? '—'}</span>
                {account.currentBalance === null ? (
                  <span className="text-amber-700 dark:text-amber-400">{t('banking.unmappedWarn')}</span>
                ) : (
                  /*
                   * BR-FIN-09-14 — "a negative balance must be shown in red
                   * with a true minus sign before the currency symbol." The
                   * minus half is `formatMoney`'s one line for the whole
                   * product (N-3: the sign OUTSIDE the symbol, `-$8,450.00`);
                   * the red half is painted here and at every other place §5b
                   * says this same figure appears — the Details tab, the
                   * reconciliation System/Cleared boxes, the FIN-12 tile and
                   * its combined total. §15 names this list as the shipped
                   * example: the Corporate Card at −$8,450.00. The sign test
                   * is `startsWith('-')` on the decimal STRING (the
                   * chart-page precedent) — money never becomes a JS number.
                   */
                  <Amount
                    value={account.currentBalance}
                    currency={account.currency}
                    className={
                      account.currentBalance.startsWith('-')
                        ? 'text-red-700 dark:text-red-400'
                        : undefined
                    }
                  />
                )}
              </span>
            </button>
          ))}
          {!isPending && (accounts ?? []).length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('banking.accountsEmpty')}</p>
          ) : null}
        </div>

        {selected ? (
          <Card className="self-start">
            <CardHeader>
              <CardTitle className="flex flex-wrap items-center gap-3 text-base">
                <span className="me-auto">{selected.name}</span>
                <nav className="flex gap-1 text-sm">
                  {(['details', 'reconciliation'] as const).map((entry) => (
                    <button
                      key={entry}
                      type="button"
                      onClick={() => open(selected.id, entry)}
                      className={`rounded-md px-3 py-1.5 font-medium ${
                        tab === entry ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      {entry === 'details' ? t('banking.tabDetails') : t('banking.tabReconciliation')}
                    </button>
                  ))}
                </nav>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {tab === 'details' ? (
                <AccountDetails key={selected.id} account={selected} />
              ) : (
                <ReconciliationTab key={selected.id} account={selected} />
              )}
            </CardContent>
          </Card>
        ) : null}
      </div>
    </div>
  );
}

/** BR-FIN-09-02's picker: cash-family assets and liabilities, plus None. */
function useLedgerOptions() {
  const { data: accounts } = useAccounts();
  return useMemo(() => {
    const header = (accounts ?? []).find((account) => account.code === '1110');
    return (accounts ?? [])
      .filter(
        (account) =>
          account.postable &&
          account.active &&
          ((header !== undefined && account.parentId === header.id) ||
            account.type === 'liability'),
      )
      .map(({ id, code, name }) => ({ id, code, name }));
  }, [accounts]);
}

function NewAccountForm({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation();
  const create = useCreateBankAccount();
  const ledgerOptions = useLedgerOptions();
  const [name, setName] = useState('');
  const [bankName, setBankName] = useState('');
  /**
   * The literal `'USD'` that used to seed this is gone. A new bank account in a
   * euro company opened denominated in dollars, and nothing on the form said
   * so — the same shape as the partner form's placeholder, on a record whose
   * currency decides what every reconciliation and cash figure means.
   *
   * Seeded from the session instead, and RE-seeded while the user has not
   * typed: `useBaseCurrency()` answers its own `'USD'` fallback until the
   * session lands, so a one-shot initial value would just relocate the bug.
   */
  const baseCurrency = useBaseCurrency();
  const [currency, setCurrency] = useState('');
  const [currencyTouched, setCurrencyTouched] = useState(false);
  useEffect(() => {
    if (currencyTouched) return;
    setCurrency(baseCurrency);
  }, [baseCurrency, currencyTouched]);
  /*
   * NO held value passed: this form creates a NEW account, so there is no
   * saved currency to keep visible. Passing the live field would append
   * whatever the user typed and silence the warning permanently — see the
   * warehouse screen's note.
   */
  const currencyCodes = useCurrencyPickerCodes();
  const [accountType, setAccountType] = useState<(typeof ACCOUNT_TYPES)[number]>('bank');
  const [ledgerAccountId, setLedgerAccountId] = useState('');
  const [openingBalance, setOpeningBalance] = useState('0.00');
  const [openingDate, setOpeningDate] = useDocumentDate();
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setError(null);
    try {
      await create.mutateAsync({
        name: name.trim(),
        ...(bankName.trim() === '' ? {} : { bankName: bankName.trim() }),
        currency,
        accountType,
        ...(ledgerAccountId === '' ? {} : { ledgerAccountId }),
        openingBalance,
        openingDate,
      });
      onDone();
    } catch (cause) {
      // This screen reached the strip by a SECOND route — it never called
      // `localizeServerMessage` at all — so it carried TWO faults, not one: a
      // refusal that arrived was printed in the server's English to an Arabic
      // reader, and a request that never came back put the browser's own
      // `Failed to fetch` where a sentence belongs. `localizeServerCause`
      // fixes both, because both come from discarding the cause.
      setError(localizeServerCause(t, cause, 'banking.errSaveFailedAccounts'));
    }
  };

  return (
    <Card>
      <CardContent className="grid gap-4">
        {error ? (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        ) : null}
        <div className="grid gap-4 sm:grid-cols-3">
          <div className="grid gap-1.5">
            <Label htmlFor="ba-name">{t('banking.fName')}</Label>
            <Input id="ba-name" value={name} onChange={(event) => setName(event.target.value)} />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="ba-bank">{t('banking.fBank')}</Label>
            <Input
              id="ba-bank"
              value={bankName}
              onChange={(event) => setBankName(event.target.value)}
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="ba-type">{t('banking.fType')}</Label>
            <NativeSelect
              id="ba-type"
              value={accountType}
              onChange={(event) =>
                setAccountType(event.target.value as (typeof ACCOUNT_TYPES)[number])
              }
            >
              {ACCOUNT_TYPES.map((type) => (
                <option key={type} value={type}>
                  {t(`banking.type.${type}`)}
                </option>
              ))}
            </NativeSelect>
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="ba-currency">{t('banking.fCurrency')}</Label>
            {/* FIN-09 field 5 is "Short text", so the value is still the text —
                the tenant's own codes are offered under it rather than gating
                it (BR-DATA-06 is the server's to enforce). */}
            <CurrencyField
              id="ba-currency"
              label={t('banking.fCurrency')}
              value={currency}
              onChange={(next) => {
                setCurrencyTouched(true);
                setCurrency(next);
              }}
              options={currencyCodes}
            />
            {/*
              FIN-09 field 5 is "Short text" too, and this screen had no
              validation path at all — so this is both the warning and the only
              thing that has ever told the user their code looks wrong. Still a
              warning: Save stays enabled below, and the server decides.
              Silent for a code the account already holds (BR-MD-01-07, via
              `currencyCodes`) — see `currencyNotOffered`.
            */}
            {currencyNotOffered(currency, currencyCodes) ? (
              <p className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
                {t('fx.currencyNotYours', { code: currency.trim().toUpperCase() })}
              </p>
            ) : null}
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="ba-ledger">{t('banking.fLedger')}</Label>
            <NativeSelect
              id="ba-ledger"
              value={ledgerAccountId}
              onChange={(event) => setLedgerAccountId(event.target.value)}
            >
              <option value="">{t('banking.ledgerNone')}</option>
              {ledgerOptions.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.code} — {option.name}
                </option>
              ))}
            </NativeSelect>
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="ba-opening">{t('banking.fOpeningBalance')}</Label>
            <Input
              id="ba-opening"
              inputMode="decimal"
              value={openingBalance}
              onChange={(event) => setOpeningBalance(event.target.value)}
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="ba-date">{t('banking.fOpeningDate')}</Label>
            <Input
              id="ba-date"
              type="date"
              value={openingDate}
              onChange={(event) => setOpeningDate(event.target.value)}
            />
          </div>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => void submit()} disabled={create.isPending || name.trim() === ''}>
            {t('common.create')}
          </Button>
          <Button variant="outline" onClick={onDone}>
            {t('common.cancel')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function AccountDetails({ account }: { account: BankAccountResponse }) {
  const { t } = useTranslation();
  const update = useUpdateBankAccount();
  const canEdit = useCan('banking.edit');
  const ledgerOptions = useLedgerOptions();
  const [form, setForm] = useState({
    name: account.name,
    bankName: account.bankName ?? '',
    accountNumber: account.accountNumber ?? '',
    branch: account.branch ?? '',
    swiftBic: account.swiftBic ?? '',
    currency: account.currency,
    accountType: account.accountType,
    ledgerAccountId: account.ledgerAccountId ?? '',
    openingBalance: account.openingBalance,
    openingDate: account.openingDate,
    isActive: account.isActive,
    notes: account.notes ?? '',
  });
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    setError(null);
    try {
      await update.mutateAsync({
        bankAccountId: account.id,
        body: {
          name: form.name.trim(),
          bankName: form.bankName.trim() === '' ? null : form.bankName.trim(),
          accountNumber: form.accountNumber.trim() === '' ? null : form.accountNumber.trim(),
          branch: form.branch.trim() === '' ? null : form.branch.trim(),
          swiftBic: form.swiftBic.trim() === '' ? null : form.swiftBic.trim(),
          currency: form.currency,
          accountType: form.accountType,
          ledgerAccountId: form.ledgerAccountId === '' ? null : form.ledgerAccountId,
          openingBalance: form.openingBalance,
          openingDate: form.openingDate,
          isActive: form.isActive,
          notes: form.notes === '' ? null : form.notes,
        },
      });
    } catch (cause) {
      setError(localizeServerCause(t, cause, 'banking.errSaveFailedAccounts'));
    }
  };

  const field = (key: keyof typeof form, value: string | boolean) =>
    setForm((state) => ({ ...state, [key]: value }));

  return (
    <div className="grid gap-4">
      {error ? (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      ) : null}
      {account.ledgerAccountId === null ? (
        <p className="rounded-md border border-amber-300 bg-amber-50 p-2 text-sm dark:border-amber-900 dark:bg-amber-950">
          {t('banking.unmappedNotice')}
        </p>
      ) : null}
      <div className="flex flex-wrap items-center gap-4 text-sm">
        <span className="text-muted-foreground">{t('banking.fCurrentBalance')}</span>
        {account.currentBalance === null ? (
          '—'
        ) : (
          <span className="font-bold">
            {/* BR-FIN-09-14: the same figure, the same red (full note at the
                account list). */}
            <Amount
              value={account.currentBalance}
              currency={account.currency}
              className={
                account.currentBalance.startsWith('-')
                  ? 'text-red-700 dark:text-red-400'
                  : undefined
              }
            />
          </span>
        )}
        <span className="text-xs text-muted-foreground">· {t('banking.autoTag')}</span>
      </div>
      <div className="grid gap-4 sm:grid-cols-3">
        <div className="grid gap-1.5">
          <Label htmlFor="d-name">{t('banking.fName')}</Label>
          <Input
            id="d-name"
            disabled={!canEdit}
            value={form.name}
            onChange={(event) => field('name', event.target.value)}
          />
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="d-bank">{t('banking.fBank')}</Label>
          <Input
            id="d-bank"
            disabled={!canEdit}
            value={form.bankName}
            onChange={(event) => field('bankName', event.target.value)}
          />
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="d-number">{t('banking.fAccountNumber')}</Label>
          <Input
            id="d-number"
            dir="ltr"
            disabled={!canEdit}
            value={form.accountNumber}
            onChange={(event) => field('accountNumber', event.target.value)}
          />
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="d-branch">{t('banking.fBranch')}</Label>
          <Input
            id="d-branch"
            disabled={!canEdit}
            value={form.branch}
            onChange={(event) => field('branch', event.target.value)}
          />
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="d-swift">{t('banking.fSwift')}</Label>
          <Input
            id="d-swift"
            dir="ltr"
            disabled={!canEdit}
            value={form.swiftBic}
            onChange={(event) => field('swiftBic', event.target.value)}
          />
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="d-type">{t('banking.fType')}</Label>
          <NativeSelect
            id="d-type"
            disabled={!canEdit}
            value={form.accountType}
            onChange={(event) => field('accountType', event.target.value)}
          >
            {ACCOUNT_TYPES.map((type) => (
              <option key={type} value={type}>
                {t(`banking.type.${type}`)}
              </option>
            ))}
          </NativeSelect>
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="d-ledger">{t('banking.fLedger')}</Label>
          <NativeSelect
            id="d-ledger"
            disabled={!canEdit}
            value={form.ledgerAccountId}
            onChange={(event) => field('ledgerAccountId', event.target.value)}
          >
            <option value="">{t('banking.ledgerNone')}</option>
            {ledgerOptions.map((option) => (
              <option key={option.id} value={option.id}>
                {option.code} — {option.name}
              </option>
            ))}
          </NativeSelect>
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="d-opening">{t('banking.fOpeningBalance')}</Label>
          <Input
            id="d-opening"
            inputMode="decimal"
            dir="ltr"
            disabled={!canEdit}
            value={form.openingBalance}
            onChange={(event) => field('openingBalance', event.target.value)}
          />
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="d-date">{t('banking.fOpeningDate')}</Label>
          <Input
            id="d-date"
            type="date"
            disabled={!canEdit}
            value={form.openingDate}
            onChange={(event) => field('openingDate', event.target.value)}
          />
        </div>
      </div>
      <label className="flex items-center gap-2 text-sm">
        <Checkbox
          checked={form.isActive}
          disabled={!canEdit}
          onCheckedChange={(checked) => field('isActive', checked === true)}
        />
        {t('banking.fActive')}
      </label>
      <div className="grid gap-1.5">
        <Label htmlFor="d-notes">{t('common.notes')}</Label>
        <Textarea
          id="d-notes"
          rows={2}
          disabled={!canEdit}
          placeholder={t('banking.notesPlaceholder')}
          value={form.notes}
          onChange={(event) => field('notes', event.target.value)}
        />
      </div>
      <Gated permission="banking.edit" reasonKey="perm.cannot.editBankAccounts">
        <div>
          <Button onClick={() => void save()} disabled={update.isPending}>
            {t('common.save')}
          </Button>
        </div>
      </Gated>
    </div>
  );
}

function ReconciliationTab({ account }: { account: BankAccountResponse }) {
  const { t } = useTranslation();
  const canEdit = useCan('banking.edit');
  /*
   * Two questions, two queries, both answered in SQL. The open period is at
   * most one row (BR-FIN-09-04), so one row is what is asked for; the
   * completed history is paged (NFR-42) — a monthly statement for the life of
   * the account is not a bounded list.
   */
  const { data: openPeriods } = useReconciliationHistory(account.id, 'in_progress', 1, 1);
  const historyPaging = usePagedState();
  const { data: history } = useReconciliationHistory(
    account.id,
    'completed',
    historyPaging.page,
    historyPaging.pageSize,
  );
  const historyRange = usePageRange(history, historyPaging.pageSize);
  const start = useStartReconciliation();
  const setStatement = useSetStatement();
  const setTicks = useSetTicks();
  const addAdjustment = useAddAdjustment();
  const complete = useCompleteReconciliation();
  const reopen = useReopenReconciliation();

  const [currentId, setCurrentId] = useState<string | null>(null);
  const [statementDate, setStatementDate] = useDocumentDate();
  const [statementBalance, setStatementBalance] = useState('');
  const [error, setError] = useState<string | null>(null);
  /**
   * ERR-FIN-09-04 — "edited the statement figure after reconciling: THE LOCK
   * RELEASES WITH NO WARNING". The reopen itself is right and stays
   * (BR-FIN-09-08 — changing the evidence withdraws the assertion; the server
   * audits it); the prototype's fault is the silence, and Q-FIN-09-1's
   * PROPOSED DEFAULT — built as exactly that, the spec's own fallback answer,
   * the question being in none of the owner's 33 open decisions — is to keep
   * the behaviour but say it out loud first. So a statement edit on a LOCKED
   * period parks the typed figure here and raises the product's ONE shared
   * confirmation box (BR-PERM-15: a new pattern must not be invented) instead
   * of mutating on blur. Confirm sends the edit — the server side of which
   * reopens, audited; cancel discards the typed figure and puts the locked
   * one back. An OPEN period keeps the plain blur-save: the warning belongs
   * to the lock, not to the field.
   */
  const [pendingStatement, setPendingStatement] = useState<string | null>(null);
  const statementInput = useRef<HTMLInputElement>(null);

  // Continue the latest open period by default — the server picked it.
  const openPeriod = openPeriods?.reconciliations[0];
  const reconciliationId = currentId ?? openPeriod?.id ?? null;
  const { data: recon } = useReconciliation(reconciliationId);

  /**
   * The reconciliation tab gets its OWN key, not the account list's, even
   * though the two carry the same words today. It is a different screen with a
   * different failure to describe, and a shared key would have to be split
   * before either could say anything the other must not.
   */
  const fail = (cause: unknown) =>
    setError(localizeServerCause(t, cause, 'banking.errSaveFailedReconcile'));

  if (account.ledgerAccountId === null) {
    // FIN-09 defects list: warn, and block reconciliation until mapped.
    return (
      <p className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm dark:border-amber-900 dark:bg-amber-950">
        {t('banking.unmappedNotice')}
      </p>
    );
  }

  const locked = recon?.status === 'completed';
  const differenceZero = recon !== undefined && Number(recon.difference) === 0;

  return (
    <div className="grid gap-4">
      {error ? (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      ) : null}

      {recon === undefined ? (
        <div className="flex flex-wrap items-end gap-3">
          <div className="grid gap-1.5">
            <Label htmlFor="r-date">{t('banking.fStatementDate')}</Label>
            <Input
              id="r-date"
              type="date"
              value={statementDate}
              onChange={(event) => setStatementDate(event.target.value)}
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="r-balance">{t('banking.fStatementBalance')}</Label>
            <Input
              id="r-balance"
              inputMode="decimal"
              dir="ltr"
              placeholder="0.00"
              value={statementBalance}
              onChange={(event) => setStatementBalance(event.target.value)}
            />
            <span className="text-xs text-muted-foreground">{t('banking.statementHint')}</span>
          </div>
          <Gated permission="banking.edit" reasonKey="perm.cannot.startReconciliation">
            <Button
              onClick={() => {
                setError(null);
                start
                  .mutateAsync({
                    bankAccountId: account.id,
                    statementDate,
                    ...(statementBalance === '' ? {} : { statementBalance }),
                  })
                  .then((created) => setCurrentId(created.id))
                  .catch(fail);
              }}
              disabled={start.isPending}
            >
              {t('banking.startReconciliation')}
            </Button>
          </Gated>
        </div>
      ) : (
        <>
          {locked ? (
            <div className="flex flex-wrap items-center gap-3 rounded-md border border-green-300 bg-green-50 p-3 text-sm dark:border-green-900 dark:bg-green-950">
              <Lock aria-hidden className="size-4 text-green-700" />
              <span className="me-auto">{t('banking.lockedBanner')}</span>
              <Gated permission="banking.approve" reasonKey="perm.cannot.reopenReconciliation">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setError(null);
                    reopen.mutateAsync(recon.id).catch(fail);
                  }}
                >
                  {t('banking.reopen')}
                </Button>
              </Gated>
            </div>
          ) : null}

          <div className="flex flex-wrap items-end gap-3">
            <div className="grid gap-1.5">
              <Label>{t('banking.fStatementDate')}</Label>
              <Ltr value={recon.statementDate} className="font-mono text-sm" />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="r-edit-balance">{t('banking.fStatementBalance')}</Label>
              <Input
                id="r-edit-balance"
                inputMode="decimal"
                dir="ltr"
                defaultValue={recon.statementBalance}
                disabled={!canEdit}
                ref={statementInput}
                onBlur={(event) => {
                  if (event.target.value === recon.statementBalance) return;
                  setError(null);
                  // ERR-FIN-09-04 / Q-FIN-09-1: on a locked period, ask first
                  // (the note at `pendingStatement`).
                  if (locked) {
                    setPendingStatement(event.target.value);
                    return;
                  }
                  setStatement
                    .mutateAsync({
                      reconciliationId: recon.id,
                      statementBalance: event.target.value,
                    })
                    .catch(fail);
                }}
              />
              <span className="text-xs text-muted-foreground">{t('banking.statementHint')}</span>
            </div>
          </div>

          {/* ERR-FIN-09-04's warning (the note at `pendingStatement`). The
              confirm verb is the existing Reopen — the act's own verb, which
              GEN-206 requires where the proposed default's trailing
              "Continue?" would not do. Escape/backdrop/Cancel all discard the
              typed figure and restore the locked one (GEN-207: do nothing). */}
          <ConfirmBox
            open={pendingStatement !== null}
            title={t('banking.reopenConfirmTitle')}
            body={t('banking.reopenConfirmBody')}
            confirmLabel={t('banking.reopen')}
            busy={setStatement.isPending}
            onConfirm={() => {
              if (pendingStatement === null) return;
              setStatement
                .mutateAsync({
                  reconciliationId: recon.id,
                  statementBalance: pendingStatement,
                })
                .then(() => setPendingStatement(null))
                .catch((cause: unknown) => {
                  setPendingStatement(null);
                  fail(cause);
                });
            }}
            onCancel={() => {
              setPendingStatement(null);
              if (statementInput.current) {
                statementInput.current.value = recon.statementBalance;
              }
            }}
          />

          {/* The difference card — the whole screen exists to drive it to zero. */}
          <div
            data-testid="difference-card"
            className={`rounded-lg border p-3 text-sm ${
              differenceZero
                ? 'border-green-300 bg-green-50 dark:border-green-900 dark:bg-green-950'
                : 'border-red-300 bg-red-50 dark:border-red-900 dark:bg-red-950'
            }`}
          >
            <p className="font-medium">
              {differenceZero ? t('banking.matchedNotice') : t('banking.discrepancyNotice')}
            </p>
            <p className="mt-1 text-lg font-bold">
              {t('banking.fDifference')}{' '}
              <Amount value={recon.difference} currency={account.currency} />
            </p>
          </div>

          <div className="grid grid-cols-3 gap-3 text-center text-sm">
            <div className="rounded-lg border p-2">
              <p className="text-xs text-muted-foreground">{t('banking.fSystemBalance')}</p>
              {/* BR-FIN-09-14 on the two BALANCES here (full note at the
                  account list). Outstanding stays uncolored on the rule's own
                  words — §5d R6: "this is NOT a balance", it is the sum of the
                  unticked movements — and Difference belongs to the card
                  above, whose red/green is reconciliation STATE, not sign. */}
              <Amount
                value={recon.systemBalance}
                currency={account.currency}
                className={
                  recon.systemBalance.startsWith('-')
                    ? 'text-red-700 dark:text-red-400'
                    : undefined
                }
              />
            </div>
            <div className="rounded-lg border p-2">
              <p className="text-xs text-muted-foreground">{t('banking.fClearedBalance')}</p>
              <Amount
                value={recon.clearedBalance}
                currency={account.currency}
                className={
                  recon.clearedBalance.startsWith('-')
                    ? 'text-red-700 dark:text-red-400'
                    : undefined
                }
              />
            </div>
            <div className="rounded-lg border p-2">
              <p className="text-xs text-muted-foreground">{t('banking.fOutstanding')}</p>
              <Amount value={recon.outstanding} currency={account.currency} />
            </div>
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                {/* The translated Cleared label — not a bare glyph (defect fixed). */}
                <TableHead>{t('banking.colCleared')}</TableHead>
                <TableHead>{t('common.date')}</TableHead>
                <TableHead>{t('banking.colDescription')}</TableHead>
                <TableHead>{t('banking.colReference')}</TableHead>
                <TableHead className="text-end">{t('banking.colAmount')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {recon.lines.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="py-6 text-center text-sm text-muted-foreground">
                    {t('banking.noMovements')}
                  </TableCell>
                </TableRow>
              ) : (
                recon.lines.map((line, index) => (
                  <TableRow
                    key={line.journalLineId ?? line.id ?? index}
                    className={line.cleared ? 'bg-green-50 dark:bg-green-950/40' : ''}
                  >
                    <TableCell>
                      {line.journalLineId !== null ? (
                        <Checkbox
                          aria-label={t('banking.colCleared')}
                          checked={line.cleared}
                          // BR-FIN-09-07: inert once reconciled.
                          disabled={locked || !canEdit}
                          onCheckedChange={(checked) => {
                            setError(null);
                            setTicks
                              .mutateAsync({
                                reconciliationId: recon.id,
                                body: {
                                  ticks: [
                                    {
                                      journalLineId: line.journalLineId!,
                                      cleared: checked === true,
                                    },
                                  ],
                                },
                              })
                              .catch(fail);
                          }}
                        />
                      ) : null}
                    </TableCell>
                    <TableCell>
                      {line.lineDate ? <Ltr value={line.lineDate} className="font-mono text-xs" /> : '—'}
                    </TableCell>
                    <TableCell className="text-sm">
                      {line.source === 'adjustment' ? (
                        <span className="flex items-center gap-2">
                          {line.adjustmentKind === 'interest'
                            ? t('banking.adjInterest')
                            : t('banking.adjBankFee')}
                          <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-800 dark:bg-amber-950 dark:text-amber-300">
                            {t('banking.adjBadge')}
                          </span>
                        </span>
                      ) : (
                        (line.description ?? '—')
                      )}
                    </TableCell>
                    <TableCell>
                      {line.reference ? <Ltr value={line.reference} className="font-mono text-xs" /> : '—'}
                    </TableCell>
                    <TableCell
                      className={`text-end ${
                        Number(line.amount) < 0
                          ? 'text-red-700 dark:text-red-400'
                          : 'text-green-700 dark:text-green-400'
                      }`}
                    >
                      <Amount value={line.amount} currency={account.currency} />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          {!locked ? (
            <div className="flex flex-wrap gap-2">
              <Gated permission="banking.approve" reasonKey="perm.cannot.adjustReconciliation">
                <Button
                  variant="outline"
                  onClick={() => {
                    setError(null);
                    addAdjustment.mutateAsync(recon.id).catch(fail);
                  }}
                  disabled={addAdjustment.isPending}
                >
                  {t('banking.addAdjustment')}
                </Button>
              </Gated>
              <Gated permission="banking.approve" reasonKey="perm.cannot.completeReconciliation">
                <Button
                  data-testid="mark-reconciled"
                  // BR-FIN-09-06 / ERR-FIN-09-01: greyed until zero.
                  disabled={!differenceZero || complete.isPending}
                  onClick={() => {
                    setError(null);
                    complete.mutateAsync(recon.id).catch(fail);
                  }}
                >
                  <CheckCircle2 aria-hidden /> {t('banking.markReconciled')}
                </Button>
              </Gated>
            </div>
          ) : null}
        </>
      )}

      {/* BR-FIN-09-13: the reconciliation history. */}
      <div>
        <p className="mb-1 text-sm font-medium">{t('banking.historyTitle')}</p>
        {(history?.total ?? 0) === 0 ? (
          <p className="text-sm text-muted-foreground">{t('banking.historyEmpty')}</p>
        ) : (
          <>
            <ul className="grid gap-1 text-sm">
              {(history?.reconciliations ?? []).map((row) => (
                <li key={row.id} className="flex items-center gap-3">
                  <CheckCircle2 aria-hidden className="size-4 text-green-700 dark:text-green-400" />
                  <Ltr value={row.statementDate} className="font-mono text-xs" />
                  <span className="me-auto text-muted-foreground">{row.completedByEmail}</span>
                  {/* BR-FIN-09-14 — a history row's figure is a balance too
                      (full note at the account list). */}
                  <Amount
                    value={row.completedBalance ?? row.statementBalance}
                    currency={account.currency}
                    className={
                      (row.completedBalance ?? row.statementBalance).startsWith('-')
                        ? 'text-red-700 dark:text-red-400'
                        : undefined
                    }
                  />
                  <span className="rounded-full bg-green-100 px-2 py-0.5 text-[11px] font-medium text-green-800 dark:bg-green-950 dark:text-green-300">
                    {t('banking.chipReconciled')}
                  </span>
                </li>
              ))}
            </ul>
            {/* NFR-42 — the range, the total, and the two steps. */}
            <div className="mt-2">
              <Pager
                page={historyPaging.page}
                pages={historyRange.pages}
                first={historyRange.first}
                last={historyRange.last}
                total={historyRange.total}
                onPage={historyPaging.setPage}
                label={t('banking.historyTitle')}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
